// ============================================================
// Tahsilat hatırlatmaları ve taksitlendirme (§5)
//
// Tahsilat oranı bir kreşin nakit akışındaki en kritik metriktir ama
// takibi elle yapılıyordu. Burada iki şey var:
//
//   1. Kademeli hatırlatma merdiveni — vade öncesi nazik, sonrası
//      giderek netleşen dört basamak
//   2. Taksitlendirme planı — `lf16`'nın notunda "taksitlendirme
//      görüşmesi bekleniyor" yazıyordu; artık yapısal
//
// Hatırlatma gönderimi §2'deki gönderim kaydını kullanır: "bu veliye
// kaç kez hatırlatma gitti" sorusunun cevabı orada.
// ============================================================

import { BUGUN } from "../config.js";
import { B, subeBulHerYerde } from "./context.js";
import { SABLONLAR } from "../data/tanimlar.js";
import { faturaDurumu, faturaTutari, tahsilEdilen } from "./invoices.js";
import { gonderimler } from "./notifications.js";
import { veliler } from "./students.js";

export { SABLONLAR };

/**
 * Hatırlatma merdiveni. `gun` vade gününe göredir:
 * negatif = vade öncesi, pozitif = vade sonrası.
 */
export const MERDIVEN = [
  { id: "nazik",   gun: -3, label: "Nazik hatırlatma", onem: "bilgi",  kanal: "eposta" },
  { id: "vade",    gun: 1,  label: "Vade geçti",       onem: "bilgi",  kanal: "eposta" },
  { id: "ikinci",  gun: 7,  label: "İkinci hatırlatma", onem: "uyari", kanal: "sms" },
  { id: "yonetim", gun: 15, label: "Yönetici uyarısı",  onem: "kritik", kanal: "sms" },
];

const gunFark = (a, b) =>
  Math.round((new Date(a + "T00:00:00Z") - new Date(b + "T00:00:00Z")) / 86400000);

// ---------- Taksitlendirme ----------


const sube = branchId => (branchId ? subeBulHerYerde(branchId) : B());

export const taksitPlanlari = branchId => sube(branchId).installmentPlans ?? [];

export const ogrencininTaksitPlani = (studentId, branchId) =>
  taksitPlanlari(branchId).find(p => p.studentId === studentId && !p.closedAt) ?? null;

/** Taksit planındaki bir sonraki ödeme. */
export function siradakiTaksit(plan) {
  return plan?.installments.find(t => !t.paidAt) ?? null;
}

export function taksitOzeti(plan) {
  if (!plan) return null;
  const odenen = plan.installments.filter(t => t.paidAt);
  return {
    toplam: plan.installments.reduce((a, t) => a + t.amount, 0),
    odenen: odenen.reduce((a, t) => a + t.amount, 0),
    kalanAdet: plan.installments.length - odenen.length,
    siradaki: siradakiTaksit(plan),
    gecikmis: plan.installments.some(t => !t.paidAt && t.dueAt < BUGUN),
  };
}

// ---------- Hatırlatma durumu ----------

/**
 * Bir fatura için hatırlatma durumu.
 * Basamak, vadeye kalan/geçen güne göre belirlenir; gönderim kaydı
 * hangilerinin gerçekten iletildiğini söyler.
 */
export function hatirlatmaDurumu(student, inv, branchId) {
  const durum = faturaDurumu(student, inv);
  if (durum === "odendi") return null;

  const gecenGun = gunFark(BUGUN, inv.due);
  const key = `hatirlatma:${student.id}:${inv.period}`;
  const gonderilenler = gonderimler(branchId).filter(g => g.key === key);

  // Uygun basamakların en ilerisi; henüz vade öncesindeyse null.
  const uygun = MERDIVEN.filter(m => gecenGun >= m.gun);
  const basamak = uygun.at(-1) ?? null;

  // Bu basamak için gönderim yapılmış mı?
  const gonderildi = basamak
    ? gonderilenler.some(g => g.basamak === basamak.id)
    : false;

  // Taksit planı varsa hatırlatma askıya alınır — anlaşma var demektir.
  const plan = ogrencininTaksitPlani(student.id, branchId);

  return {
    key,
    inv,
    student,
    durum,
    gecenGun,
    basamak,
    gonderildi,
    gonderimSayisi: gonderilenler.length,
    sonGonderim: gonderilenler.at(-1)?.at ?? null,
    askida: Boolean(plan),
    plan,
    kalan: faturaTutari(student, inv) - tahsilEdilen(inv),
  };
}

/** Şubedeki hatırlatma gerektiren faturalar — en gecikmişi başta. */
export function hatirlatmaGerekenler(branchId) {
  const t = sube(branchId);
  return t.payments
    .map(inv => {
      const s = t.students.find(x => x.id === inv.studentId);
      return s ? hatirlatmaDurumu(s, inv, t.id) : null;
    })
    .filter(Boolean)
    .filter(h => h.basamak && !h.gonderildi && !h.askida)
    .sort((a, b) => b.gecenGun - a.gecenGun);
}

/** Tüm açık faturaların hatırlatma görünümü — Ödemeler ekranı için. */
export function hatirlatmaTablosu(branchId) {
  const t = sube(branchId);
  return t.payments
    .map(inv => {
      const s = t.students.find(x => x.id === inv.studentId);
      return s ? hatirlatmaDurumu(s, inv, t.id) : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.gecenGun - a.gecenGun);
}

// ---------- Şablon ----------

/** Şablondaki yer tutucuları doldurur. */
export function metinUret(basamakId, { student, inv, kalan, branchId }) {
  const sablon = SABLONLAR[basamakId] ?? SABLONLAR.vade;
  const veli = veliler(student, branchId)[0];
  const t = sube(branchId);

  return sablon
    .replaceAll("{veli}", veli?.name ?? "Sayın velimiz")
    .replaceAll("{ogrenci}", student.name)
    .replaceAll("{donem}", inv.period)
    .replaceAll("{tutar}", `${kalan.toLocaleString("tr-TR")} ₺`)
    .replaceAll("{vade}", inv.due)
    .replaceAll("{kurum}", t.name);
}

/** Özet — panel ve Ödemeler başlığı için. */
export function hatirlatmaOzeti(branchId) {
  const tablo = hatirlatmaTablosu(branchId);
  const bekleyen = tablo.filter(h => h.basamak && !h.gonderildi && !h.askida);

  return {
    acikFatura: tablo.length,
    bekleyenHatirlatma: bekleyen.length,
    askidaki: tablo.filter(h => h.askida).length,
    toplamAcik: tablo.reduce((a, h) => a + h.kalan, 0),
    enGecikmis: tablo[0]?.gecenGun ?? 0,
    yonetimSeviyesi: bekleyen.filter(h => h.basamak.id === "yonetim").length,
  };
}
